import { getCSRFToken } from '@shared/cookie';
import Modal from '@shared/ui-modal';
import PasswordlessForm from '@shared/passwordless-form';
import { T_ } from '@shared/translation';
import uiIcon from '@shared/ui-icon';
import { createLocationField } from '@shared/location-field';
import template from 'lodash-es/template';
import 'css/flux/modules/ModalLocalOneClick.less';
import 'css/vendor/GoogleMapsAutocomplete.less';
import createOneClickForm from '@shared/local-jobs-one-click-form';
import { T37423UniformCopy } from 'server-data/local-jobs-landing-page';
import modalHeaderTemplate from './local-oneclick-modal-header.html';
import modalFooterTemplate from './local-oneclick-modal-footer.html';
import modalBodyTemplate from './local-oneclick-modal-body.html';

const MODAL_ID = 'local-1cpp-location-modal';

export default function openLocalOneClickModal({ subcategory, isLoggedIn }) {
  const oneClickForm = createOneClickForm('local-1cpp-project-form');
  oneClickForm.setSubCategory(subcategory);
  showLocationModal(isLoggedIn, oneClickForm);
}

function showLocationModal(isLoggedIn, oneClickForm) {
  const locationModal = new Modal(
    MODAL_ID,
    {
      header: template(modalHeaderTemplate)({ T_ }),
      body: template(modalBodyTemplate)({
        T_,
        uiIcon,
        isLoggedIn,
        modalID: MODAL_ID,
      }),
      footer: template(modalFooterTemplate)({
        T_,
        modalID: MODAL_ID,
        isLoggedIn,
        T37423UniformCopy,
      }),
    },
    {
      backdropClose: false,
      extraClass: 'ModalLocalOneClick is-ModalLocalOneClick-step1-active',
      onHide: resetModalForm,
    },
  );
  const locationField = createLocationField({
    inputFieldId: 'local-1c-modal-location-input',
    locationBtnId: 'local-1c-modal-location-geocode',
    parentForm: oneClickForm,
    errorAlertId: 'local-1c-modal-location-error',
  });

  if (!isLoggedIn) {
    initPasswordLessForm(oneClickForm, locationField);
  } else {
    setPostProjectButtonListener(oneClickForm, locationField);
  }
  locationModal.show();
}

function initPasswordLessForm(oneClickForm, locationField) {
  const passwordLessForm = new PasswordlessForm({
    onSignupModeActivated: () => {
      const signupTncField = document.getElementById(
        `${MODAL_ID}-signup-tnc-copy`,
      );
      if (signupTncField) {
        signupTncField.classList.remove('is-hidden');
      }
    },
    onSignupModeDeactivated: () => {
      const signupTncField = document.getElementById(
        `${MODAL_ID}-signup-tnc-copy`,
      );
      if (signupTncField) {
        signupTncField.classList.add('is-hidden');
      }
    },
    submissionCallback: form => {
      oneClickForm.setEmail(form.email.value);
      oneClickForm.setNewUserName(form.newusername.value);
      oneClickForm.setNewPassword(form.newuserpasswd.value);

      function submit(data) {
        fireQTS('TriedFormSubmit');
        if (data.status === 'success') {
          oneClickForm.submit();
          fireQTS('PostALocalJob');
          passwordLessForm.submitEl.textContent = T_('Submitting...');
          passwordLessForm.submitEl.setAttribute('disabled', 'disabled');
        } else {
          passwordLessForm.showLoginError(data.msg);
        }
      }

      if (passwordLessForm.isLoginMode()) {
        fireQTS('TriedLoginThroughAjax');
        const formData = new FormData();
        formData.append('username', form.email.value);
        formData.append('passwd', form.newuserpasswd.value);
        formData.append('csrf_token', getCSRFToken());
        fetch('/ajax/login.php', {
          method: 'POST',
          credentials: 'same-origin',
          body: formData,
        })
          .then(response => response.json())
          .then(data => submit(data));
      } else {
        submit({ status: 'success' });
      }
    },
  });
  passwordLessForm.initForm(MODAL_ID);
  passwordLessForm.reset();

  const backBtn = document.querySelector(`#${MODAL_ID}-back`);
  if (backBtn) {
    backBtn.addEventListener('click', goToLocationFormPane);
  }

  const submitBtn = document.querySelector(`#${MODAL_ID}-submit`);
  if (submitBtn) {
    submitBtn.addEventListener('click', () => {
      passwordLessForm.submitForm();
    });
  }

  const signupBtn = document.querySelector(`#${MODAL_ID}-signup`);
  if (signupBtn) {
    signupBtn.addEventListener('click', () => {
      goToPasswordlessFormPane(locationField);
    });
  }
}

function setPostProjectButtonListener(oneClickForm, locationField) {
  const postBtn = document.querySelector(`#${MODAL_ID}-post`);
  if (postBtn) {
    postBtn.addEventListener('click', () => {
      if (locationField.validate()) {
        postBtn.setAttribute('disabled', 'disabled');
        oneClickForm.submit();
      }
    });
  }
}

function fireQTS(label) {
  _t.push([
    'user_action',
    {
      section: 'LocalJobsLandingPage',
      subsection: 'CategoryCardsOneClick',
      label,
    },
  ]);
}

function resetModalForm() {
  const postBtn = document.querySelector(`#${MODAL_ID}-post`);
  postBtn.removeAttribute('disabled');
  const locationForm = document.getElementById(`${MODAL_ID}-location-form`);
  if (locationForm) {
    locationForm.reset();
  }
  const signupForm = document.getElementById(`${MODAL_ID}-form`);
  if (signupForm) {
    signupForm.reset();
    goToLocationFormPane();
  }
}

function goToLocationFormPane() {
  const modalElement = document.getElementById(MODAL_ID);
  modalElement.classList.remove('is-ModalLocalOneClick-step2-active');
  modalElement.classList.add('is-ModalLocalOneClick-step1-active');

  const signupBtn = document.querySelector(`#${MODAL_ID}-signup`);
  signupBtn.classList.remove('is-hidden');

  const submitBtn = document.querySelector(`#${MODAL_ID}-submit`);
  submitBtn.classList.add('is-hidden');

  const backBtn = document.querySelector(`#${MODAL_ID}-back`);
  backBtn.classList.add('is-hidden');

  const progressStep = document.getElementById('progress-step-1');
  progressStep.classList.remove('is-ProgressSteps-step-completed');
}

function goToPasswordlessFormPane(locationField) {
  if (locationField.validate()) {
    const modalElement = document.getElementById(MODAL_ID);
    modalElement.classList.remove('is-ModalLocalOneClick-step1-active');
    modalElement.classList.add('is-ModalLocalOneClick-step2-active');

    const signupBtn = document.querySelector(`#${MODAL_ID}-signup`);
    signupBtn.classList.add('is-hidden');

    const submitBtn = document.querySelector(`#${MODAL_ID}-submit`);
    submitBtn.classList.remove('is-hidden');

    const backBtn = document.querySelector(`#${MODAL_ID}-back`);
    backBtn.classList.remove('is-hidden');

    const progressStep = document.getElementById('progress-step-1');
    progressStep.classList.add('is-ProgressSteps-step-completed');
  }
}
